import { Dayjs } from 'dayjs'
import { Duration } from 'dayjs/plugin/duration'

import {
    AcceptedDayInput,
    accumulateDuration,
    DATE_TEMPLATES,
    day,
    duration,
    emptyDuration,
    format,
    isBefore,
    isBetween,
    isSame,
    isSameDate,
    isSameOrAfter,
    subtract,
    ZERO_DURATION_TOKEN,
} from '~/astra'
import { ScheduledSurgery, SchedulingSurgery, Surgery, UnScheduledSurgery } from '~/storm'

import { getDaysDifferenceText } from '../getDaysDifferenceText'
import { isNotNullish, isNullish } from '../guards'

export const FINISH_CODE_COMPLETED = 'OR'

/**
 * Structural surgery shape accepted by the identity helpers. Storm-wide so
 * both Storm and Denormalizer (`Dips*Surgery`, `SchedulingSurgery`) callers
 * satisfy it, since Denormalizer types are subtypes of their Storm counterparts.
 */
export type AnySurgery = ScheduledSurgery | UnScheduledSurgery

/**
 * Currently, we differentiate between scheduled and unscheduled surgeries by the presence of the 'id' field.
 */
export function isScheduledSurgery<T extends AnySurgery>(surgery: T): surgery is Extract<T, ScheduledSurgery> {
    return 'id' in surgery
}

export function isUnScheduledSurgery<T extends AnySurgery>(surgery: T): surgery is Extract<T, UnScheduledSurgery> {
    return 'bookingId' in surgery && !('sagaId' in surgery)
}

export function isSchedulingSurgery<T extends AnySurgery>(surgery: T): surgery is T & { sagaId: string; plannedTheatreInTime: string } {
    return 'sagaId' in surgery && typeof surgery.sagaId === 'string' && 'plannedTheatreInTime' in surgery && typeof surgery.plannedTheatreInTime === 'string'
}

/**
 * Checks if a scheduled surgery was cancelled (finishCode exists but is not 'OR')
 */
export function isCancelledSurgery(surgery: ScheduledSurgery): boolean {
    const finishCode = surgery.finishInformation?.finishCode
    return !!finishCode && finishCode !== FINISH_CODE_COMPLETED
}

/**
 * Gets the (booking) id, irrespective of whether it's a scheduled, unscheduled or scheduling surgery.
 */
export function getBookingIdOrId(surgery: AnySurgery): string {
    return isScheduledSurgery(surgery) ? surgery.id : surgery.bookingId
}

export type OrgUnitGuids = {
    department_guid: string | null
    section_guid: string | null
    ward_guid: string | null
    location_guid: string | null
    patient_guid: string | null
}

/**
 * Gets the DIPS org-unit guids (department/section/ward/location) of the surgery's contact,
 * plus the patient guid, for attaching to SurgeryFolderMetadata saves.
 */
export function getOrgUnitGuids(surgery: Surgery): OrgUnitGuids {
    return {
        department_guid: surgery.contact?.department?.guid ?? null,
        section_guid: surgery.contact?.section?.guid ?? null,
        ward_guid: surgery.contact?.ward?.guid ?? null,
        location_guid: surgery.contact?.location?.guid ?? null,
        patient_guid: surgery.patient?.personProxyGuid ?? null,
    }
}

const MISSING_CLOCK_TIME = '--:--'

export function formatClockTime(time: string | null | undefined) {
    return isNotNullish(time) ? format(day(time), DATE_TEMPLATES.time) : MISSING_CLOCK_TIME
}

/**
 * Gets a formatted check-in time of the surgery
 */
export const formatCheckInTime = (surgery: Surgery) => formatClockTime(surgery.contact?.checkInTime)

/** Clock time, prefixed with calendar days before `relativeTo` when they differ. */
export function formatCheckInTimeRelativeToStart(checkInTime: string | null | undefined, relativeTo: string | null | undefined) {
    if (isNullish(checkInTime)) return MISSING_CLOCK_TIME

    const time = formatClockTime(checkInTime)
    if (isNullish(relativeTo)) return time

    const daysDifferenceText = getDaysDifferenceText(checkInTime, relativeTo)
    return daysDifferenceText ? `(${daysDifferenceText}) ${time}` : time
}

/**
 * Gets the room duration of the surgery (stored as iso 8601) as dayjs Duration.
 */
export function getSurgeryRoomDuration(surgery: Surgery): Duration {
    const roomDuration = isScheduledSurgery(surgery) ? surgery.plannedDuration : surgery.plannedTimes.duration
    return roomDuration ? duration(roomDuration) : duration(ZERO_DURATION_TOKEN)
}

/**
 * Gets the room duration for the supplied surgeries
 */
export const getSurgeriesRoomDuration = (surgeries: UnScheduledSurgery[] = []): Duration =>
    surgeries.map(getSurgeryRoomDuration).reduce(accumulateDuration, emptyDuration)

/**
 * Gets the knife time duration of the surgery (stored as iso 8601) as dayjs Duration.
 */
export function getKnifeTime(surgery: Surgery): Duration | null {
    const procedureDuration = isScheduledSurgery(surgery) ? surgery.plannedProcedureDuration : surgery.plannedTimes.procedureDuration
    return procedureDuration ? duration(procedureDuration) : null
}

/**
 * Gets the diagnosis group name of the surgery.
 */
export const getDiagnosisGroupName = (surgery: Surgery): string | null | undefined =>
    isScheduledSurgery(surgery) ? surgery.contact?.diagnosisGroupName : surgery.contact.diagnosisGroup?.name

/**
 * Gets the level of care npr name of the surgery.
 */
export const getLevelOfCareNprName = (surgery: Surgery): string | null | undefined =>
    isScheduledSurgery(surgery) ? surgery.contact?.levelOfCareNpr?.nprCodeName : surgery.contact.levelOfCareNpr?.name

/**
 * Returns plannedTheatreInTime (start time) for a surgery, using checkInTime as a fallback.
 */
export function getScheduledTime(surgery: Surgery): Dayjs {
    return day('plannedTheatreInTime' in surgery ? surgery.plannedTheatreInTime : surgery.contact?.checkInTime)
}

export function isSurgeryInDateRange(start: AcceptedDayInput, end?: AcceptedDayInput) {
    if (end === undefined) {
        return (surgery: Surgery) => isSameOrAfter(getScheduledTime(surgery), start, 'day')
    }
    return (surgery: Surgery) => isBetween(getScheduledTime(surgery), start, end, 'day', '[]')
}

/**
 * Determines if a surgery cancellation qualifies as a "same-day" cancellation.
 *
 * A cancellation is considered "same-day" if:
 * 1. The surgery was cancelled (finishCode exists and is not 'OR'), AND
 * 2. The cancellation occurred either:
 *    - On the same calendar day as the scheduled surgery, OR
 *    - On the day before, but at or after 14:00
 *
 * This extended window (from 14:00 the day before) accounts for late-day
 * cancellations that effectively impact same-day scheduling and resource planning.
 *
 * @param surgery - The scheduled surgery to check
 * @param previousDayAsSameDayHour - The hour threshold for day-before cancellations (default: 14)
 * @returns true if the surgery meets the same-day cancellation criteria
 */
export function isSameDayCancellation(surgery: ScheduledSurgery, previousDayAsSameDayHour: number = 14): boolean {
    if (!isCancelledSurgery(surgery) || !surgery.finishInformation?.finishTime) return false

    const scheduledTime = getScheduledTime(surgery)
    const finishTime = day(surgery.finishInformation.finishTime)

    // Check if cancelled on the same day as scheduled
    if (isSameDate(finishTime, scheduledTime)) {
        return true
    }

    // Check if cancelled on the day before, at or after the threshold hour
    const dayBeforeScheduled = subtract(scheduledTime, 1, 'day')
    const isCancelledDayBefore = isSameDate(finishTime, dayBeforeScheduled)
    if (!isCancelledDayBefore) {
        return false
    }
    const cancelledAtOrAfterThreshold = finishTime.hour() >= previousDayAsSameDayHour

    return cancelledAtOrAfterThreshold
}

export const sortByWaitingTime = (surgery1: UnScheduledSurgery, surgery2: UnScheduledSurgery) =>
    day(surgery1.creationTime).unix() - day(surgery2.creationTime).unix()

export function isSurgeryLetterSent<T extends AnySurgery>(surgery: T): surgery is Extract<T, ScheduledSurgery> {
    return isScheduledSurgery(surgery) && surgery.letterStatus === 2
}

type SortableSurgery = ScheduledSurgery | SchedulingSurgery

const sortSurgeriesByNationalId = (surgery1: SortableSurgery, surgery2: SortableSurgery) => {
    const idA = surgery1.patient?.nationalId
    const idB = surgery2.patient?.nationalId

    // Sort nullish last
    if (!idA && !idB) return 0
    if (!idA) return 1
    if (!idB) return -1

    return idA.localeCompare(idB)
}

export function getSurgeryOrder(surgery: object): number | undefined {
    return 'order' in surgery && typeof surgery.order === 'number' ? surgery.order : undefined
}

function comparePlannedTheatreInTime(surgery1: SortableSurgery, surgery2: SortableSurgery): number {
    const timeA = surgery1.plannedTheatreInTime
    const timeB = surgery2.plannedTheatreInTime

    if (!timeA && !timeB) {
        return sortSurgeriesByNationalId(surgery1, surgery2)
    }

    if (!timeA) return 1
    if (!timeB) return -1

    const dayA = day(timeA)
    const dayB = day(timeB)

    if (isSame(dayA, dayB, 'minute')) {
        return sortSurgeriesByNationalId(surgery1, surgery2)
    }

    return isBefore(dayA, dayB, 'minute') ? -1 : 1
}

// Sort by DIPS `order` when present, then planned theatre-in time, then national id. Nullish keys sort last.
export const sortSurgeriesByOrderThenTheatreInTime = (surgery1: SortableSurgery, surgery2: SortableSurgery) => {
    const orderA = getSurgeryOrder(surgery1)
    const orderB = getSurgeryOrder(surgery2)

    if (orderA !== orderB) {
        if (orderA === undefined) return 1
        if (orderB === undefined) return -1
        return orderA - orderB
    }

    return comparePlannedTheatreInTime(surgery1, surgery2)
}
